/**
 * WHAT: Authentication context providing user state, login/logout, and RBAC permission helpers.
 * WHY:  DAMA enforces role-based access at the UI level — every nav item, view, and action
 *       checks permissions via this context, ensuring DBAs only see their technology and
 *       attesters only see their attestation queue.
 */

import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

/**
 * WHAT: Context provider that manages authenticated user state and exposes permission checks.
 * WHY:  Persists the logged-in user to localStorage and provides technology-specific permission
 *       helpers (e.g., canManageConnections, canViewComplianceReports) so that every component
 *       can enforce RBAC without duplicating permission logic.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem('dama_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('dama_user');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('dama_user', JSON.stringify(userData));
  };

  const logout = (navigate) => {
    // Best-effort: flush the server-side session so the session cookie is invalidated
    // server-side (clearing localStorage alone wouldn't revoke an issued session). Don't
    // block the client-side logout on a network error.
    try {
      const base = (typeof window !== 'undefined' && window.__DAMA_CONFIG__ && window.__DAMA_CONFIG__.apiBaseUrl)
        || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      fetch(`${base}/api/databases/auth/logout/`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    } catch (e) { /* ignore */ }

    setUser(null);
    localStorage.removeItem('dama_user');
    if (navigate) {
      navigate('/login');
    }
  };

  const hasPermission = (permission) => {
    if (!user || !user.role) return false;
    // Dev admins have all permissions
    if (user.role.is_dev_admin === true) return true;
    return user.role[permission] === true;
  };

  const isRole = (roleId) => {
    if (!user) return false;
    return user.role_id === roleId;
  };

  // Supported database technologies
  const TECHNOLOGIES = ['oracle', 'mssql', 'mysql', 'cassandra', 'postgresql'];

  /**
   * Check if user can manage connections for a specific technology
   * @param {string} technology - The database technology (oracle, mssql, etc.)
   * @returns {boolean}
   */
  const canManageTechnology = (technology, action) => {
    if (!user || !user.role) return false;
    // Dev admins have all permissions
    if (user.role.is_dev_admin) return true;
    const permissionKey = `can_${action}_${technology.toLowerCase()}_connections`;
    return user.role[permissionKey] === true;
  };

  /**
   * Check if user can manage connections for a specific technology
   */
  const canManageConnections = (technology) => {
    if (!user || !user.role) return false;
    if (user.role.is_dev_admin) return true;
    return user.role[`can_manage_${technology.toLowerCase()}_connections`] === true;
  };

  /**
   * Check if user can manage refresh jobs for a specific technology
   */
  const canManageRefreshJobs = (technology) => {
    if (!user || !user.role) return false;
    if (user.role.is_dev_admin) return true;
    return user.role[`can_manage_${technology.toLowerCase()}_refresh_jobs`] === true;
  };

  /**
   * Check if user can manage privilege mappings for a specific technology
   */
  const canManagePrivilegeMappings = (technology) => {
    if (!user || !user.role) return false;
    if (user.role.is_dev_admin) return true;
    return user.role[`can_manage_${technology.toLowerCase()}_privilege_mappings`] === true;
  };

  /**
   * Check if user can view compliance reports for a specific technology
   */
  const canViewComplianceReports = (technology) => {
    if (!user || !user.role) return false;
    if (user.role.is_dev_admin) return true;
    return user.role[`can_view_${technology.toLowerCase()}_compliance_reports`] === true;
  };

  /**
   * Get list of technologies the user has any access to
   */
  const getAccessibleTechnologies = () => {
    if (!user || !user.role) return [];
    if (user.role.is_dev_admin) return TECHNOLOGIES;

    return TECHNOLOGIES.filter(tech =>
      canManageConnections(tech) ||
      canManageRefreshJobs(tech) ||
      canManagePrivilegeMappings(tech) ||
      canViewComplianceReports(tech)
    );
  };

  /**
   * Check if user can manage any technology's connections
   */
  const canManageAnyConnections = () => {
    if (!user || !user.role) return false;
    if (user.role.is_dev_admin) return true;
    return TECHNOLOGIES.some(tech => canManageConnections(tech));
  };

  const value = {
    user,
    login,
    logout,
    hasPermission,
    isRole,
    loading,
    // Technology-specific permission helpers
    canManageTechnology,
    canManageConnections,
    canManageRefreshJobs,
    canManagePrivilegeMappings,
    canViewComplianceReports,
    getAccessibleTechnologies,
    canManageAnyConnections
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * WHAT: Hook to access auth context (user, permissions, login/logout) from any component.
 * WHY:  Provides a clean API for components to check permissions and react to auth state
 *       without prop-drilling, enforcing the rule that useAuth must be inside AuthProvider.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
